/**
 * GuestSignupGate
 *
 * The JAI mascot pop-up that asks a guest to create an account once they have
 * used their free AI questions. Mounted once, in AppShell, so it works from
 * every page without any of them knowing about it.
 *
 * How it gets triggered: the backend refuses the request with 429
 * GUEST_SIGNUP_REQUIRED (the real limit lives there — a frontend-only limit is
 * not a limit), services/api.js spots that response and fires a window event,
 * and this component listens for it. The number of free questions is
 * deliberately NOT written here, so there is only one place to change it.
 *
 * It is a wall, not a suggestion — the question is not answered until they
 * sign up — but it is a warm one:
 *   - the question they just typed is saved, and sent for them after signup
 *     (utils/pendingQuestion.js), so signing up feels like the door opening;
 *   - "Not now" closes it and they can keep browsing; the next AI attempt
 *     simply brings it back.
 *
 * Copy states the real stake, like GuestPrompt.jsx does: a guest account lives
 * in this browser only. No emoji — this is UI chrome and none are on the
 * allowlist for it (see config/statGlyphs.js).
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { GUEST_LIMIT_EVENT } from '../services/api'
import { rememberPendingQuestion } from '../utils/pendingQuestion'
import { trackEvent } from '../analytics'
import MascotDialog from './draft/MascotDialog'

export default function GuestSignupGate() {
  const { isGuest } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  // Whose allowance ran out — see announceGuestLimit in services/api.js.
  const [scope, setScope] = useState('guest')
  const shownRef = useRef(false)

  useEffect(() => {
    const onLimit = async (event) => {
      // Belt and braces: the backend only sends this code to guests, but a
      // registered user must never see a sign-up wall.
      if (!isGuest) return

      // Only the Ask JAI page can resume a question after signup — on a tool
      // page the bar's question isn't something we can replay, so save just
      // the location and let them pick up from there.
      const path = window.location.pathname
      const question = path.startsWith('/chat') ? event.detail?.question : ''
      await rememberPendingQuestion({ path: path + window.location.search, question })

      // One dialog (and one analytics event) even if several AI calls are
      // refused at once. A ref, not a state updater: updaters can run twice
      // in dev, which would double-count the event.
      const hitScope = event.detail?.scope === 'network' ? 'network' : 'guest'
      if (!shownRef.current) trackEvent('guest_limit_hit', { page: path, scope: hitScope })
      shownRef.current = true
      setScope(hitScope)
      setOpen(true)
    }
    window.addEventListener(GUEST_LIMIT_EVENT, onLimit)
    return () => window.removeEventListener(GUEST_LIMIT_EVENT, onLimit)
  }, [isGuest])

  if (!open || !isGuest) return null

  const go = (target, to) => () => {
    trackEvent('guest_gate_cta_click', { target })
    shownRef.current = false
    setOpen(false)
    if (to) navigate(to)
  }

  // 'network' means the shared pool for this internet connection is spent, so
  // this person may not have asked anything yet — "you've used your free
  // questions" would be untrue for them, and blaming them would be unkind.
  const fromNetwork = scope === 'network'

  return (
    <MascotDialog
      state={fromNetwork ? 'happy' : 'excited'}
      size={140}
      // Wider than the default so the three footer buttons sit on one line.
      width={540}
      headline={fromNetwork ? 'Create a free account to keep going' : "You're getting the hang of JAI"}
      body={fromNetwork
        ? "The free questions on this network are used up for today. A free account gets you past that. Your leagues, notes and chat history come with you, and JAI will pick up your question right where you left off."
        : "You've used your free questions. Create a free account to keep going. Your leagues, notes and chat history come with you, and JAI will pick up your question right where you left off."}
      onClose={go('dismiss')}
      actions={[
        { label: 'Create free account', primary: true, onClick: go('signup', '/signup') },
        { label: 'I have an account', onClick: go('login', '/login') },
        { label: 'Not now', onClick: go('dismiss') },
      ]}
    />
  )
}
